import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { List, message, Pagination, Row, Col, Select } from 'antd';
import moment from 'moment';

import Spinner from '../components/UI/Spinner/Spinner';
import PageHeader from '../components/UI/PageHeader/PageHeader';
import VehicleItem from '../components/Vehicles/VehicleItem';
import VehicleForm from '../components/Vehicles/VehicleForm/VehicleForm';
import VehiclesSearchBar from '../components/Vehicles/VehiclesSearchBar';

import {
  fetchVehicles,
  addVehicle,
  editVehicle,
  removeVehicle,
} from '../store/actions/vehicles-actions';
import { uploadVehicleImage } from '../store/actions/vehicleImages-actions';
import { fetchCarHouseUsers } from '../store/actions/carHouseUser-actions';
import { fetchCarHouseVehicles } from '../store/actions/carHouseVehicles-actions';
import { fetchUserVehicles } from '../store/actions/userVehicles-actions';
import { fetchCarHouses } from '../store/actions/carHouse-actions';

import { vehicleActions } from '../store/slices/vehicles-slice';
import { carHouseVehiclesActions } from '../store/slices/carHouseVehicles-slice';
import { userVehiclesActions } from '../store/slices/usersVehicles-slice';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

const Vehicles = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  const [openForm, setOpenForm] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showButton, setShowButton] = useState(false);
  const loggedInUser = useSelector((state) => state.auth.loggedInUser);

  const [perPage, setPerPage] = useState(12);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const isPurchaseVehicles = location.pathname.includes('purchase-vehicles');

  const [vehicleType, setVehicleType] = useState(null);

  const { carHouseUsers } = useSelector((state) => state.carHouseUser);
  const { carHouses } = useSelector((state) => state.carHouse);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [carHouseId, setCarHouseId] = useState(null);
  const [selectedCarHouseId, setSelectedCarHouseId] = useState(null);

  const {
    isLoading: isLoadingAllVehicles,
    vehicles: allVehicles,
    count: allVehiclesCount,
    currentPage: allVehiclesPage,
    searchParams: allVehiclesSearchParams,
    sortBy: allVehiclesSortBy,
  } = useSelector((state) => state.vehicle);

  const {
    isLoading: isLoadingCarHouseVehicles,
    carHouseVehicles,
    count: carHouseCount,
    currentPage: carHousePage,
    searchParams: carHouseSearchParams,
    sortBy: carHouseSortBy,
  } = useSelector((state) => state.carHouseVehicles);

  const {
    isLoading: isLoadingUserVehicles,
    userVehicles,
    count: userCount,
    currentPage: userPage,
    searchParams: userSearchParams,
    sortBy: userSortBy,
  } = useSelector((state) => state.userVehicles);

  const vehicleData = {
    carHouse: {
      vehicles: carHouseVehicles,
      page: carHousePage,
      count: carHouseCount,
      searchParams: carHouseSearchParams,
      sortBy: carHouseSortBy,
      isLoading: isLoadingCarHouseVehicles,
    },
    user: {
      vehicles: userVehicles,
      page: userPage,
      count: userCount,
      searchParams: userSearchParams,
      sortBy: userSortBy,
      isLoading: isLoadingUserVehicles,
    },
    all: {
      vehicles: allVehicles,
      page: allVehiclesPage,
      count: allVehiclesCount,
      searchParams: allVehiclesSearchParams,
      sortBy: allVehiclesSortBy,
      isLoading: isLoadingAllVehicles,
    },
  };

  const { vehicles, page, count, searchParams, sortBy, isLoading } =
    vehicleData[vehicleType] || vehicleData.all;

  useEffect(() => {
    if (loggedInUser?.role?.name === 'carHouseAdmin' && isPurchaseVehicles) {
      setCarHouseId(loggedInUser.carHouseId);
      setSelectedCarHouseId(loggedInUser.carHouseId);
      setVehicleType('carHouse');
    } else if (loggedInUser?.role?.name === 'user' && isPurchaseVehicles) {
      setSelectedUserId(loggedInUser.id);
      setVehicleType('user');
    } else {
      setVehicleType('all');
    }
  }, [loggedInUser, isPurchaseVehicles]);

  useEffect(() => {
    if (
      loggedInUser.role.name === 'root' ||
      loggedInUser.role.name === 'admin'
    ) {
      setShowButton(true);
    }

    const controller = new AbortController();
    const { signal } = controller;
    const pagination = { page: page, total: perPage };

    const options = {
      params: {
        ...searchParams,
        ...(sortBy && { sortBy }),
      },
      signal,
    };

    if (vehicleType === 'carHouse') {
      dispatch(
        fetchCarHouseVehicles({
          carHouseId: carHouseId,
          pagination,
          options,
        })
      );
    } else if (vehicleType === 'user') {
      dispatch(
        fetchUserVehicles({
          userId: selectedUserId,
          pagination,
          options,
        })
      );
    } else {
      dispatch(fetchVehicles({ pagination, options }));
    }

    return () => controller.abort();
  }, [
    dispatch,
    loggedInUser,
    page,
    searchParams,
    perPage,
    sortBy,
    vehicleType,
    selectedUserId,
    carHouseId,
  ]);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    if (loggedInUser.role.name === 'root') {
      const pagination = { page: 1, total: 100 };
      dispatch(fetchCarHouses({ pagination, options: { signal } }));
    }
    return () => controller.abort();
  }, [dispatch, loggedInUser]);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    if (selectedCarHouseId) {
      const userPagination = { page: 1, total: 100 };
      dispatch(
        fetchCarHouseUsers({
          carHouseId: selectedCarHouseId,
          pagination: userPagination,
          options: { signal },
        })
      );
    }
    return () => controller.abort();
  }, [dispatch, selectedCarHouseId]);

  const onAddVehicle = () => {
    setSelectedVehicle(null);
    setOpenForm(true);
  };

  const onEditVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setOpenForm(true);
  };

  const addVehicleHandler = async (body, files) => {
    try {
      const vehicle = await dispatch(addVehicle(body));
      if (vehicle.error) {
        const errorMessage =
          vehicle?.payload || t('vehicles.errorAdding');
        message.error(errorMessage);
        return;
      }

      if (files.length) {
        await uploadImageFiles(files, vehicle.payload.id, true);
      }
      setVehicleType('all');
      dispatch(
        fetchVehicles({
          pagination: { page: page, total: perPage },
        })
      );
      message.success(t('vehicles.addVehicleSuccess'));
      setOpenForm(false);
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        t('vehicles.errorAdding');
      message.error(errorMessage);
    }
  };

  const editVehicleHandler = async (body, files) => {
    try {
      dispatch(editVehicle({ vehicleId: selectedVehicle.id, body }));

      if (files.length) {
        await uploadImageFiles(files, selectedVehicle.id, false);
      }
      message.success(t('vehicles.updateMessage'));
      setOpenForm(false);
    } catch (err) {
      setOpenForm(false);
    }
  };

  const uploadImageFiles = async (files, vehicleId, isNewVehicle) => {
    try {
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        const image = files[i];
        formData.append('image', image.originFileObj);
        // Only append imageType if it exists
        if (image.imageType) {
          formData.append('type', image.imageType);
        }

        // Set order for the first image
        if (isNewVehicle && i === 0) {
          formData.append('order', 1);
        }

        const uploadedImage = await dispatch(
          uploadVehicleImage({ vehicleId, body: formData })
        );

        const imageDetails = uploadedImage.payload;
        if (isNewVehicle && i === 0) {
          // Dispatch action to set the image to the vehicle
          dispatch(
            vehicleActions.setImageToVehicle({ vehicleId, image: imageDetails })
          );
        }
      }
    } catch (error) {
      console.error('Error uploading images:', error);
    }
  };

  const deleteVehicleHandler = async (id) => {
    try {
      dispatch(removeVehicle(id));
      message.success('Successfully removed vehicle');
    } catch (err) {
      console.log(err);
    }
  };

  const changePageHandler = (pageNumber) => {
    if (vehicleType === 'carHouse') {
      dispatch(carHouseVehiclesActions.setCurrentPage(pageNumber));
    } else if (vehicleType === 'user') {
      dispatch(userVehiclesActions.setCurrentPage(pageNumber));
    } else {
      dispatch(vehicleActions.setCurrentPage(pageNumber));
    }
  };

  const onSearch = (searchData) => {
    const { userId, carHouseId, ...rest } = searchData || {};

    let data = {};
    Object.keys(rest).forEach((key) => {
      if (rest[key] !== undefined && rest[key] !== '') {
        data[key] = rest[key];
      }
    });

    if (userId && carHouseId) {
      setSelectedUserId(userId);
      setCarHouseId(carHouseId);
      setVehicleType('user');
      dispatch(userVehiclesActions.setSearchParams(data));
      dispatch(userVehiclesActions.setCurrentPage(1));
      return;
    } else if (userId) {
      setSelectedUserId(userId);
      setVehicleType('user');
      dispatch(userVehiclesActions.setSearchParams(data));
      dispatch(userVehiclesActions.setCurrentPage(1));
    } else if (carHouseId) {
      setCarHouseId(carHouseId);
      setVehicleType('carHouse');
      dispatch(carHouseVehiclesActions.setSearchParams(data));
      dispatch(carHouseVehiclesActions.setCurrentPage(1));
      return;
    } else {
      if (isPurchaseVehicles) {
        if (loggedInUser?.role?.name === 'carHouseAdmin') {
          setVehicleType('carHouse');
          dispatch(carHouseVehiclesActions.setSearchParams(data));
          dispatch(carHouseVehiclesActions.setCurrentPage(1));
        } else {
          setVehicleType('user');
          dispatch(userVehiclesActions.setSearchParams(data));
          dispatch(userVehiclesActions.setCurrentPage(1));
        }
      } else {
        setVehicleType('all');
        dispatch(vehicleActions.setSearchParams(data));
        dispatch(vehicleActions.setCurrentPage(1));
      }
      return;
    }
  };

  const onRangeDate = (dates) => {
    if (dates) {
      setFromDate(moment(dates[0]).startOf('day'));
      setToDate(moment(dates[1]).endOf('day'));
    } else {
      setFromDate(null);
      setToDate(null);
    }
  };

  const onSortChange = (value) => {
    dispatch(vehicleActions.setSortBy(value));
    dispatch(vehicleActions.setCurrentPage(1));
  };

  let content;
  isLoading
    ? (content = <Spinner />)
    : (content = (
        <>
          <PageHeader
            title={t('vehicles.vehicles')}
            onAdd={onAddVehicle}
            showButton={showButton}
            buttonText={t('vehicles.addNewVehicle')}
          />
          <Row justify="space-between" style={{ padding: '10px' }}>
            <Col>
              <h4>{t('vehicles.vehiclesFound', { count })}
                
              </h4>
            </Col>
            <Col>
              <Select
                value={sortBy || undefined}
                placeholder={t('vehicles.sortBy')}
                onChange={onSortChange}
                style={{ width: 158 }}
                allowClear
              >
                <Option value="make">{t('vehicles.make')}</Option>
                <Option value="model">{t('vehicles.model')}</Option>
              </Select>
            </Col>
          </Row>
          <List
            grid={{
              gutter: 16,
              xs: 1,
              sm: 2,
              md: 3,
              lg: 4,
              xl: 4,
              xxl: 4,
            }}
            dataSource={vehicles}
            renderItem={(vehicle) => (
              <List.Item>
                <VehicleItem
                  vehicle={vehicle}
                  onEditVehicle={onEditVehicle}
                  onDeleteVehicle={deleteVehicleHandler}
                />
              </List.Item>
            )}
          />
          <Pagination
            current={page}
            total={count}
            pageSize={perPage}
            showQuickJumper
            showSizeChanger={false}
            onChange={changePageHandler}
          />
        </>
      ));

  return (
    <>
      <VehiclesSearchBar
        onSearch={onSearch}
        onRangeDate={onRangeDate}
        initialValues={searchParams}
        fromDate={fromDate}
        carHouses={loggedInUser.role.name === 'root' ? carHouses : []}
        userList={carHouseUsers}
        setSelectedCarHouseId={(carHouse) => setSelectedCarHouseId(carHouse)}
        role={loggedInUser.role.name}
        isPurchaseVehicles={isPurchaseVehicles}
      />
      {content}
      <VehicleForm
        open={openForm}
        selectedVehicle={selectedVehicle}
        title="vehicle"
        onCancel={() => setOpenForm(false)}
        onEditVehicle={onEditVehicle}
        addVehicle={addVehicleHandler}
        editVehicle={editVehicleHandler}
      />
    </>
  );
};

export default Vehicles;
